/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */

const IV_LEN = 12; // Should match Node.js: GCM recommended nonce size
const TAG_LEN = 16; // Should match Node.js: AES-GCM standard tag length

// Helper: base64url encode (expects Uint8Array, returns string)
function base64urlEncode(uint8Array: Uint8Array) {
  let str = "";
  uint8Array.forEach((byte) => {
    str += String.fromCharCode(byte);
  });
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Helper: base64url decode (expects string, returns ArrayBuffer)
function base64urlDecode(str: string) {
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) {
    // Pad if necessary for atob
    str += "=";
  }
  const byteString = atob(str);
  const buffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(buffer);
  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }
  return buffer;
}

// Derive a 32-byte key using SHA-256(secret + salt)
async function getKey(secret: string, salt: string) {
  const enc = new TextEncoder();
  // Concatenate secret and salt before hashing
  const keyMaterialData = enc.encode(secret + salt);
  const keyDigest = await window.crypto.subtle.digest(
    "SHA-256",
    keyMaterialData
  );

  return window.crypto.subtle.importKey(
    "raw",
    keyDigest, // This is an ArrayBuffer
    { name: "AES-GCM" }, // Algorithm for the key
    false, // not extractable
    ["encrypt", "decrypt"]
  );
}

export async function obfuscate(value: string, secret: string, salt: string) {
  const key = await getKey(secret, salt);
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LEN)); // iv is Uint8Array
  const enc = new TextEncoder();

  // Web Crypto's AES-GCM encrypt output is (Ciphertext || Tag)
  const ciphertextWithTagBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, // iv must be ArrayBufferView
    key,
    enc.encode(value) // plaintext
  ); // ciphertextWithTagBuffer is an ArrayBuffer

  // Separate Ciphertext and Tag from Web Crypto's combined output
  // to match the Node.js structure: IV || Ciphertext || Tag
  const ciphertextBuffer = ciphertextWithTagBuffer.slice(
    0,
    ciphertextWithTagBuffer.byteLength - TAG_LEN
  );
  const tagBuffer = ciphertextWithTagBuffer.slice(
    ciphertextWithTagBuffer.byteLength - TAG_LEN
  );

  // Create a Uint8Array for IV || Ciphertext || Tag
  const dataToEncode = new Uint8Array(
    IV_LEN + ciphertextBuffer.byteLength + TAG_LEN
  );
  dataToEncode.set(iv, 0); // iv is Uint8Array
  dataToEncode.set(new Uint8Array(ciphertextBuffer), IV_LEN);
  dataToEncode.set(
    new Uint8Array(tagBuffer),
    IV_LEN + ciphertextBuffer.byteLength
  );

  return base64urlEncode(dataToEncode).split("").reverse().join("");
}

export async function deobfuscate(
  obfuscatedValue: string,
  secret: string,
  salt: string
) {
  try {
    const base64UrlString = obfuscatedValue.split("").reverse().join("");
    // base64urlDecode returns an ArrayBuffer
    const decodedDataBuffer = base64urlDecode(base64UrlString);

    if (decodedDataBuffer.byteLength < IV_LEN + TAG_LEN) {
      console.error("Deobfuscation failed: Decoded data too short.");
      return null;
    }

    const iv = decodedDataBuffer.slice(0, IV_LEN); // This is an ArrayBuffer
    const ciphertext = decodedDataBuffer.slice(
      IV_LEN,
      decodedDataBuffer.byteLength - TAG_LEN
    ); // ArrayBuffer
    const tag = decodedDataBuffer.slice(decodedDataBuffer.byteLength - TAG_LEN); // ArrayBuffer

    // For Web Crypto's decrypt, the tag needs to be part of the ciphertext buffer
    const ciphertextWithTag = new Uint8Array(
      ciphertext.byteLength + tag.byteLength
    );
    ciphertextWithTag.set(new Uint8Array(ciphertext), 0);
    ciphertextWithTag.set(new Uint8Array(tag), ciphertext.byteLength);

    const key = await getKey(secret, salt);
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv }, // iv (ArrayBuffer)
      key,
      ciphertextWithTag.buffer // Pass the ArrayBuffer view of (Ciphertext || Tag)
    );
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error("Deobfuscation error (Browser):", error);
    return null;
  }
}
